import { logDataDogError } from '../../../../../errors/utils/errors'
import type { GraphQLErrorContext, NetworkErrorResult } from './types'
import type { ApolloError } from '@apollo/client'

export const formatGraphQLErrorMessage = (error: ApolloError): string => {
  if (!error) {
    return 'Unknown error occurred'
  }

  const errorParts: string[] = []

  if (error.graphQLErrors?.length > 0) {
    error.graphQLErrors.forEach((gqlError, index) => {
      let errorMsg = gqlError.message
      if (gqlError.locations && gqlError.locations.length > 0) {
        const location = gqlError.locations[0]
        errorMsg += ` (line ${location.line}, column ${location.column})`
      }
      if (gqlError.path && gqlError.path.length > 0) {
        errorMsg += ` at path: ${gqlError.path.join('.')}`
      }
      errorParts.push(`GraphQL Error ${index + 1}: ${errorMsg}`)
    })
  }

  if (error.networkError) {
    let networkMsg = `Network Error: ${error.networkError.message}`
    if ('statusCode' in error.networkError) {
      networkMsg += ` (HTTP ${error.networkError.statusCode})`
    }
    if ('result' in error.networkError && error.networkError.result) {
      try {
        const result = error.networkError.result as NetworkErrorResult
        if (result.errors && Array.isArray(result.errors)) {
          result.errors.forEach((err, index: number) => {
            if (err.message) {
              errorParts.push(`Server Error ${index + 1}: ${err.message}`)
            }
          })
        }
      } catch {
        // ignore
      }
    }
    if (errorParts.length === 0) {
      errorParts.push(networkMsg)
    }
  }

  if (errorParts.length === 0) {
    errorParts.push(`Unknown Error: ${error.message}`)
  }

  return errorParts.join('\n')
}

export const emitGraphQLError = (
  error: ApolloError,
  context?: GraphQLErrorContext,
): void => {
  const formattedMessage = formatGraphQLErrorMessage(error)

  const contextLabelParts: string[] = []
  if (context?.operation) contextLabelParts.push(context.operation)
  if (context?.queryName) contextLabelParts.push(context.queryName)
  if (context?.resourceName) contextLabelParts.push(context.resourceName)
  if (context?.objectUid) contextLabelParts.push(String(context.objectUid))

  const label = contextLabelParts.join(':') || 'GraphQL'

  logDataDogError(
    new Error(
      `GraphQL Layer - ${
        context?.operation ? context.operation : 'operation'
      } failed${label ? ` for ${label}` : ''}`,
    ),
    {
      component: `GraphQL Layer - ${label}`,
      description: formattedMessage,
      graphqlErrors: error.graphQLErrors,
      networkError: error.networkError,
      ...context,
    },
  )
}
